import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import ImageUpload from '../components/ui/ImageUpload';
import { 
  Users, 
  Home, 
  Calendar as CalendarIcon,
  TrendingUp,
  User,
  Shield,
  Pencil,
  Loader2,
  Phone,
  Mail,
  MapPin,
  ExternalLink,
  MessageSquare,
  Clock,
  Star,
  AlertCircle,
  CalendarX,
  Trash2,
  FileText,
  Inbox
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getFullImageUrl } from '../lib/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPage = () => {
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [contactRequests, setContactRequests] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editSection, setEditSection] = useState('');
  const [editContent, setEditContent] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Service detail sheet
  const [selectedService, setSelectedService] = useState(null);
  const [serviceSheetOpen, setServiceSheetOpen] = useState(false);
  
  // Calendar management
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [blockReason, setBlockReason] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, propertiesRes, servicesRes, reviewsRes, blockedRes, contactRes, quoteRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/stats`),
        axios.get(`${API_URL}/admin/users`),
        axios.get(`${API_URL}/properties`),
        axios.get(`${API_URL}/services`),
        axios.get(`${API_URL}/reviews`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/blocked-dates`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/contact-requests`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/quote-requests`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/invoices`).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setProperties(propertiesRes.data);
      setServices(servicesRes.data);
      setReviews(reviewsRes.data);
      setBlockedDates(blockedRes.data);
      setContactRequests(contactRes.data);
      setQuoteRequests(quoteRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleBlockDate = async () => {
    if (!selectedCalendarDate) return;
    
    const dateStr = format(selectedCalendarDate, 'yyyy-MM-dd');
    
    try {
      await axios.post(`${API_URL}/blocked-dates`, {
        date: dateStr,
        reason: blockReason
      });
      toast.success(language === 'fr' ? 'Date bloquée' : 'Date blocked');
      setBlockReason('');
      setSelectedCalendarDate(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };
  
  const handleUnblockDate = async (dateStr) => {
    try {
      await axios.delete(`${API_URL}/blocked-dates/${dateStr}`);
      toast.success(language === 'fr' ? 'Date débloquée' : 'Date unblocked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };
  
  const isDateBlocked = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return blockedDates.some(b => b.date === dateStr);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`);
      toast.success(language === 'fr' ? 'Utilisateur supprimé' : 'User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    try {
      await axios.delete(`${API_URL}/admin/properties/${propertyId}`);
      toast.success(language === 'fr' ? 'Propriété supprimée' : 'Property deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const handleStatusChange = async (serviceId, newStatus, e) => {
    e?.stopPropagation();
    try {
      await axios.put(`${API_URL}/admin/services/${serviceId}/status`, { status: newStatus });
      toast.success(t('serviceUpdated'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const openServiceDetail = (service) => {
    setSelectedService(service);
    setServiceSheetOpen(true);
  };

  const getOwnerForService = (service) => {
    return users.find(u => u.id === service.owner_id);
  };

  const getPropertyForService = (service) => {
    return properties.find(p => p.id === service.property_id);
  };

  const openEditDialog = async (section) => {
    try {
      const response = await axios.get(`${API_URL}/content/${section}`);
      setEditContent(response.data.content || {});
      setEditSection(section);
      setEditDialogOpen(true);
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleSaveContent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/content/${editSection}`, editContent);
      toast.success(language === 'fr' ? 'Contenu mis à jour' : 'Content updated');
      setEditDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      accepted: 'badge-info',
      completed: 'badge-success',
      cancelled: 'badge-error',
      active: 'badge-success',
      inactive: 'badge-error',
    };
    return badges[status] || 'badge-info';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">{t('admin')}</h1>
        <p className="text-muted-foreground mt-1">
          {language === 'fr' 
            ? 'Gérez tous les utilisateurs, propriétés et services'
            : 'Manage all users, properties and services'
          }
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-base" data-testid="stat-total-users">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats?.total_users || 0}</p>
                <p className="text-sm text-muted-foreground">{t('totalUsers')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base" data-testid="stat-total-properties">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Home className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats?.total_properties || 0}</p>
                <p className="text-sm text-muted-foreground">{t('totalProperties')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base" data-testid="stat-total-services">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats?.total_services || 0}</p>
                <p className="text-sm text-muted-foreground">{t('totalServices')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base" data-testid="stat-pending-services">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats?.pending_services || 0}</p>
                <p className="text-sm text-muted-foreground">{t('pendingServices')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content Edit Buttons */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="font-heading">{t('editContent')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => openEditDialog('hero')} data-testid="edit-hero-btn">
              <Pencil className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Page d\'accueil' : 'Landing Page'}
            </Button>
            <Button variant="outline" onClick={() => openEditDialog('profile')} data-testid="edit-profile-btn">
              <Pencil className="h-4 w-4 mr-2" />
              {language === 'fr' ? 'Mon Profil' : 'My Profile'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="services" className="w-full">
        <TabsList className="mb-6" data-testid="admin-tabs">
          <TabsTrigger value="services">
            {t('services')} ({services.length})
          </TabsTrigger>
          <TabsTrigger value="users">
            {t('allUsers')} ({users.length})
          </TabsTrigger>
          <TabsTrigger value="properties">
            {t('properties')} ({properties.length})
          </TabsTrigger>
          <TabsTrigger value="calendar">
            <CalendarX className="h-4 w-4 mr-1" />
            {language === 'fr' ? 'Calendrier' : 'Calendar'}
          </TabsTrigger>
          <TabsTrigger value="reviews">
            {language === 'fr' ? 'Avis' : 'Reviews'} ({reviews.length})
          </TabsTrigger>
          <TabsTrigger value="contacts">
            <Inbox className="h-4 w-4 mr-1" />
            {language === 'fr' ? 'Contacts' : 'Contacts'} ({contactRequests.length})
          </TabsTrigger>
          <TabsTrigger value="quotes">
            <FileText className="h-4 w-4 mr-1" />
            {language === 'fr' ? 'Devis' : 'Quotes'} ({quoteRequests.length})
          </TabsTrigger>
          <TabsTrigger value="invoices">
            <FileText className="h-4 w-4 mr-1" />
            {language === 'fr' ? 'Factures' : 'Invoices'} ({invoices.length})
          </TabsTrigger>
        </TabsList>

        {/* Services Tab */}
        <TabsContent value="services">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading">{t('allServices')}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {language === 'fr' 
                  ? 'Cliquez sur une ligne pour voir les détails du propriétaire'
                  : 'Click on a row to see owner details'
                }
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('serviceType')}</TableHead>
                      <TableHead>{language === 'fr' ? 'Propriété' : 'Property'}</TableHead>
                      <TableHead>{language === 'fr' ? 'Propriétaire' : 'Owner'}</TableHead>
                      <TableHead>{language === 'fr' ? 'Contact' : 'Contact'}</TableHead>
                      <TableHead>{t('selectDate')}</TableHead>
                      <TableHead>{t('status')}</TableHead>
                      <TableHead>{t('updateStatus')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {services.map((service) => {
                      const property = getPropertyForService(service);
                      const owner = getOwnerForService(service);
                      return (
                        <TableRow 
                          key={service.id} 
                          className="cursor-pointer hover:bg-accent/50"
                          onClick={() => openServiceDetail(service)}
                          data-testid={`admin-service-row-${service.id}`}
                        >
                          <TableCell className="font-medium">{t(service.service_type)}</TableCell>
                          <TableCell>{property?.name || '-'}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <User className="h-4 w-4 text-primary" />
                              </div>
                              <span>{owner?.name || '-'}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {owner?.email && (
                                <a 
                                  href={`mailto:${owner.email}`} 
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-1 hover:bg-primary/10 rounded"
                                  title={owner.email}
                                >
                                  <Mail className="h-4 w-4 text-primary" />
                                </a>
                              )}
                              {(owner?.phone || property?.phone) && (
                                <a 
                                  href={`tel:${owner?.phone || property?.phone}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-1 hover:bg-primary/10 rounded"
                                  title={owner?.phone || property?.phone}
                                >
                                  <Phone className="h-4 w-4 text-primary" />
                                </a>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {format(new Date(service.scheduled_date), 'PP', { locale: language === 'fr' ? fr : undefined })}
                          </TableCell>
                          <TableCell>
                            <span className={getStatusBadge(service.status)}>{t(service.status)}</span>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Select
                              value={service.status}
                              onValueChange={(value) => handleStatusChange(service.id, value)}
                            >
                              <SelectTrigger className="w-[140px]" data-testid={`status-select-${service.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">{t('pending')}</SelectItem>
                                <SelectItem value="accepted">{t('accepted')}</SelectItem>
                                <SelectItem value="completed">{t('completed')}</SelectItem>
                                <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading">{t('allUsers')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('name')}</TableHead>
                      <TableHead>{t('email')}</TableHead>
                      <TableHead>{t('phone')}</TableHead>
                      <TableHead>{language === 'fr' ? 'Rôle' : 'Role'}</TableHead>
                      <TableHead>{language === 'fr' ? 'Actions' : 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              {u.role === 'admin' ? (
                                <Shield className="h-4 w-4 text-primary" />
                              ) : (
                                <User className="h-4 w-4 text-primary" />
                              )}
                            </div>
                            {u.name}
                          </div>
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>{u.phone || '-'}</TableCell>
                        <TableCell>
                          <span className={u.role === 'admin' ? 'badge-info' : 'badge-success'}>
                            {u.role === 'admin' ? t('admin_role') : t('owner')}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <a 
                              href={`mailto:${u.email}`}
                              className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                              title={language === 'fr' ? 'Envoyer un email' : 'Send email'}
                            >
                              <Mail className="h-4 w-4 text-primary" />
                            </a>
                            {u.phone && (
                              <a 
                                href={`tel:${u.phone}`}
                                className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                                title={language === 'fr' ? 'Appeler' : 'Call'}
                              >
                                <Phone className="h-4 w-4 text-primary" />
                              </a>
                            )}
                            {u.role !== 'admin' && (
                              <button
                                onClick={() => handleDeleteUser(u.id)}
                                className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                                title={language === 'fr' ? 'Supprimer' : 'Delete'}
                                data-testid={`admin-delete-user-${u.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Properties Tab */}
        <TabsContent value="properties">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading">{t('allProperties')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('propertyName')}</TableHead>
                      <TableHead>{t('address')}</TableHead>
                      <TableHead>{language === 'fr' ? 'Propriétaire' : 'Owner'}</TableHead>
                      <TableHead>{t('propertyType')}</TableHead>
                      <TableHead>{t('status')}</TableHead>
                      <TableHead>{language === 'fr' ? 'Actions' : 'Actions'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {properties.map((property) => {
                      const owner = users.find(u => u.id === property.owner_id);
                      return (
                        <TableRow key={property.id} data-testid={`admin-property-row-${property.id}`}>
                          <TableCell className="font-medium">{property.name}</TableCell>
                          <TableCell>{property.address}</TableCell>
                          <TableCell>{owner?.name || '-'}</TableCell>
                          <TableCell className="capitalize">{t(property.property_type)}</TableCell>
                          <TableCell>
                            <span className={getStatusBadge(property.status)}>{t(property.status)}</span>
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={() => handleDeleteProperty(property.id)}
                              className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                              title={language === 'fr' ? 'Supprimer' : 'Delete'}
                              data-testid={`admin-delete-property-${property.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calendar Management Tab */}
        <TabsContent value="calendar">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Calendar to block dates */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <CalendarX className="h-5 w-5" />
                  {language === 'fr' ? 'Bloquer des dates' : 'Block Dates'}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' 
                    ? 'Sélectionnez une date pour la bloquer. Les propriétaires ne pourront pas réserver ces jours.'
                    : 'Select a date to block it. Property owners won\'t be able to book on these days.'}
                </p>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedCalendarDate}
                  onSelect={setSelectedCalendarDate}
                  locale={language === 'fr' ? fr : undefined}
                  modifiers={{
                    blocked: (date) => isDateBlocked(date)
                  }}
                  modifiersStyles={{
                    blocked: { backgroundColor: '#fee2e2', color: '#dc2626', fontWeight: 'bold' }
                  }}
                  className="mx-auto border rounded-lg p-3"
                  data-testid="admin-calendar"
                />
                
                {selectedCalendarDate && (
                  <div className="mt-4 space-y-3">
                    <div className="p-3 bg-accent rounded-lg">
                      <p className="font-medium">
                        {language === 'fr' ? 'Date sélectionnée:' : 'Selected date:'}{' '}
                        {format(selectedCalendarDate, 'PPP', { locale: language === 'fr' ? fr : undefined })}
                      </p>
                    </div>
                    
                    {isDateBlocked(selectedCalendarDate) ? (
                      <Button 
                        onClick={() => handleUnblockDate(format(selectedCalendarDate, 'yyyy-MM-dd'))}
                        variant="outline"
                        className="w-full"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {language === 'fr' ? 'Débloquer cette date' : 'Unblock this date'}
                      </Button>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <Label>{language === 'fr' ? 'Raison (optionnel)' : 'Reason (optional)'}</Label>
                          <Input
                            value={blockReason}
                            onChange={(e) => setBlockReason(e.target.value)}
                            placeholder={language === 'fr' ? 'Ex: Vacances, Maintenance...' : 'Ex: Holiday, Maintenance...'}
                          />
                        </div>
                        <Button onClick={handleBlockDate} className="w-full btn-primary">
                          <CalendarX className="h-4 w-4 mr-2" />
                          {language === 'fr' ? 'Bloquer cette date' : 'Block this date'}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* List of blocked dates */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="font-heading">
                  {language === 'fr' ? 'Dates bloquées' : 'Blocked Dates'} ({blockedDates.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {blockedDates.length === 0 ? (
                  <div className="text-center py-12">
                    <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">
                      {language === 'fr' ? 'Aucune date bloquée' : 'No blocked dates'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {blockedDates.sort((a, b) => a.date.localeCompare(b.date)).map((blocked) => (
                      <div key={blocked.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                        <div>
                          <p className="font-medium text-red-800">
                            {format(new Date(blocked.date), 'PPP', { locale: language === 'fr' ? fr : undefined })}
                          </p>
                          {blocked.reason && (
                            <p className="text-sm text-red-600">{blocked.reason}</p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-800 hover:bg-red-100"
                          onClick={() => handleUnblockDate(blocked.date)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Reviews Tab */}
        <TabsContent value="reviews">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading">{language === 'fr' ? 'Avis clients' : 'Customer Reviews'}</CardTitle>
            </CardHeader>
            <CardContent>
              {reviews.length === 0 ? (
                <div className="text-center py-12">
                  <Star className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">
                    {language === 'fr' ? 'Aucun avis pour le moment' : 'No reviews yet'}
                  </p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {reviews.map((review) => {
                    const reviewer = users.find(u => u.id === review.user_id);
                    return (
                      <div key={review.id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">{reviewer?.name || 'Anonymous'}</p>
                              <div className="flex items-center gap-1">
                                {[1,2,3,4,5].map((star) => (
                                  <Star 
                                    key={star} 
                                    className={`h-4 w-4 ${star <= review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`} 
                                  />
                                ))}
                              </div>
                            </div>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {format(new Date(review.created_at), 'PP', { locale: language === 'fr' ? fr : undefined })}
                          </span>
                        </div>
                        <p className="mt-3 text-muted-foreground">{review.comment}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Requests Tab */}
        <TabsContent value="contacts">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading">{language === 'fr' ? 'Demandes de contact' : 'Contact Requests'}</CardTitle>
            </CardHeader>
            <CardContent>
              {contactRequests.length === 0 ? (
                <div className="text-center py-12">
                  <Inbox className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">{language === 'fr' ? 'Aucune demande de contact' : 'No contact requests'}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'fr' ? 'Nom' : 'Name'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Email' : 'Email'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Téléphone' : 'Phone'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Service' : 'Service'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Message' : 'Message'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Date' : 'Date'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contactRequests.map((req, idx) => (
                        <TableRow key={idx} data-testid={`contact-row-${idx}`}>
                          <TableCell className="font-medium">{req.first_name} {req.last_name}</TableCell>
                          <TableCell>
                            <a href={`mailto:${req.email}`} className="text-primary hover:underline">{req.email}</a>
                          </TableCell>
                          <TableCell>
                            <a href={`tel:${req.phone}`} className="text-primary hover:underline">{req.phone}</a>
                          </TableCell>
                          <TableCell className="capitalize">{req.service}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{req.message || '-'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {req.created_at ? format(new Date(req.created_at), 'PP', { locale: language === 'fr' ? fr : undefined }) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quote Requests Tab */}
        <TabsContent value="quotes">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading">{language === 'fr' ? 'Demandes de devis' : 'Quote Requests'}</CardTitle>
            </CardHeader>
            <CardContent>
              {quoteRequests.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">{language === 'fr' ? 'Aucune demande de devis' : 'No quote requests'}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'fr' ? 'Nom' : 'Name'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Email' : 'Email'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Téléphone' : 'Phone'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Type' : 'Type'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Fréquence' : 'Frequency'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Services' : 'Services'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Message' : 'Message'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Date' : 'Date'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {quoteRequests.map((req, idx) => (
                        <TableRow key={idx} data-testid={`quote-row-${idx}`}>
                          <TableCell className="font-medium">{req.name}</TableCell>
                          <TableCell>
                            <a href={`mailto:${req.email}`} className="text-primary hover:underline">{req.email}</a>
                          </TableCell>
                          <TableCell>
                            <a href={`tel:${req.phone}`} className="text-primary hover:underline">{req.phone}</a>
                          </TableCell>
                          <TableCell className="capitalize">{req.property_type}</TableCell>
                          <TableCell className="capitalize">{req.frequency}</TableCell>
                          <TableCell className="max-w-[200px]">
                            {req.services?.length > 0 ? req.services.join(', ') : '-'}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">{req.message || '-'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {req.created_at ? format(new Date(req.created_at), 'PP', { locale: language === 'fr' ? fr : undefined }) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading">{language === 'fr' ? 'Factures' : 'Invoices'}</CardTitle>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">{language === 'fr' ? 'Aucune facture' : 'No invoices'}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'fr' ? 'N° Facture' : 'Invoice #'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Propriétaire' : 'Owner'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Montant' : 'Amount'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Statut' : 'Status'}</TableHead>
                        <TableHead>{language === 'fr' ? 'Date' : 'Date'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((inv, idx) => {
                        const owner = users.find(u => u.id === inv.owner_id);
                        return (
                          <TableRow key={idx} data-testid={`invoice-row-${idx}`}>
                            <TableCell className="font-medium font-mono">{inv.invoice_number}</TableCell>
                            <TableCell>{owner?.name || inv.owner_id}</TableCell>
                            <TableCell className="font-semibold">{inv.amount}€</TableCell>
                            <TableCell>
                              <span className={getStatusBadge(inv.status)}>{inv.status}</span>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {inv.issue_date ? format(new Date(inv.issue_date), 'PP', { locale: language === 'fr' ? fr : undefined }) : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Service Detail Sheet */}
      <Sheet open={serviceSheetOpen} onOpenChange={setServiceSheetOpen}>
        <SheetContent className="sm:max-w-lg" data-testid="service-detail-sheet">
          {selectedService && (() => {
            const owner = getOwnerForService(selectedService);
            const property = getPropertyForService(selectedService);
            return (
              <>
                <SheetHeader>
                  <SheetTitle className="font-heading">
                    {language === 'fr' ? 'Détails de la demande' : 'Service Request Details'}
                  </SheetTitle>
                  <SheetDescription>
                    {t(selectedService.service_type)}
                  </SheetDescription>
                </SheetHeader>
                
                <div className="mt-6 space-y-6">
                  {/* Owner Info */}
                  <div className="p-4 bg-accent/50 rounded-xl">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <User className="h-5 w-5 text-primary" />
                      {language === 'fr' ? 'Propriétaire' : 'Owner'}
                    </h3>
                    {owner ? (
                      <div className="space-y-3">
                        <p className="font-medium text-lg">{owner.name}</p>
                        
                        <div className="flex flex-col gap-2">
                          {owner.email && (
                            <a 
                              href={`mailto:${owner.email}`}
                              className="flex items-center gap-2 text-primary hover:underline"
                              data-testid="owner-email-link"
                            >
                              <Mail className="h-4 w-4" />
                              {owner.email}
                            </a>
                          )}
                          {(owner.phone || property?.phone) && (
                            <a 
                              href={`tel:${owner.phone || property?.phone}`}
                              className="flex items-center gap-2 text-primary hover:underline"
                              data-testid="owner-phone-link"
                            >
                              <Phone className="h-4 w-4" />
                              {owner.phone || property?.phone}
                            </a>
                          )}
                        </div>

                        <div className="flex gap-2 mt-4">
                          {owner.email && (
                            <a href={`mailto:${owner.email}`}>
                              <Button size="sm" variant="outline" data-testid="email-owner-btn">
                                <Mail className="h-4 w-4 mr-2" />
                                {language === 'fr' ? 'Envoyer un email' : 'Send Email'}
                              </Button>
                            </a>
                          )}
                          {(owner.phone || property?.phone) && (
                            <a href={`tel:${owner.phone || property?.phone}`}>
                              <Button size="sm" className="btn-primary" data-testid="call-owner-btn">
                                <Phone className="h-4 w-4 mr-2" />
                                {language === 'fr' ? 'Appeler' : 'Call'}
                              </Button>
                            </a>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <AlertCircle className="h-4 w-4" />
                        <span>{language === 'fr' ? 'Propriétaire non trouvé' : 'Owner not found'}</span>
                      </div>
                    )}
                  </div>

                  {/* Property Info */}
                  <div className="p-4 border rounded-xl">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <Home className="h-5 w-5 text-primary" />
                      {language === 'fr' ? 'Propriété' : 'Property'}
                    </h3>
                    <div className="space-y-2">
                      <p className="font-medium">{property?.name || 'N/A'}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {property?.address || 'N/A'}
                      </p>
                      {property?.email && (
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          {property.email}
                        </p>
                      )}
                      {property?.phone && (
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          {property.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Service Info */}
                  <div className="p-4 border rounded-xl">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary" />
                      {language === 'fr' ? 'Service' : 'Service'}
                    </h3>
                    <div className="space-y-2">
                      <p className="font-medium">{t(selectedService.service_type)}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {format(new Date(selectedService.scheduled_date), 'PPP', { locale: language === 'fr' ? fr : undefined })} - {t(selectedService.time_slot)}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={getStatusBadge(selectedService.status)}>
                          {t(selectedService.status)}
                        </span>
                      </div>
                      {selectedService.description && (
                        <div className="mt-3 p-3 bg-muted rounded-lg">
                          <p className="text-sm">{selectedService.description}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick Status Update */}
                  <div className="p-4 border rounded-xl">
                    <h3 className="font-heading font-semibold mb-3">{t('updateStatus')}</h3>
                    <Select
                      value={selectedService.status}
                      onValueChange={(value) => {
                        handleStatusChange(selectedService.id, value);
                        setSelectedService({ ...selectedService, status: value });
                      }}
                    >
                      <SelectTrigger data-testid="sheet-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">{t('pending')}</SelectItem>
                        <SelectItem value="accepted">{t('accepted')}</SelectItem>
                        <SelectItem value="completed">{t('completed')}</SelectItem>
                        <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

      {/* Edit Content Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="edit-content-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editSection === 'hero' 
                ? (language === 'fr' ? 'Modifier la page d\'accueil' : 'Edit Landing Page')
                : (language === 'fr' ? 'Modifier mon profil' : 'Edit My Profile')
              }
            </DialogTitle>
          </DialogHeader>
          
          {editSection === 'hero' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Titre (Anglais)' : 'Title (English)'}</Label>
                <Input
                  value={editContent.title_en || ''}
                  onChange={(e) => setEditContent({ ...editContent, title_en: e.target.value })}
                  data-testid="edit-hero-title-en"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Titre (Français)' : 'Title (French)'}</Label>
                <Input
                  value={editContent.title_fr || ''}
                  onChange={(e) => setEditContent({ ...editContent, title_fr: e.target.value })}
                  data-testid="edit-hero-title-fr"
                />
              </div>
            </div>
          )}
          
          {editSection === 'profile' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('name')}</Label>
                <Input
                  value={editContent.name || ''}
                  onChange={(e) => setEditContent({ ...editContent, name: e.target.value })}
                  data-testid="edit-profile-name"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Titre (Anglais)' : 'Title (English)'}</Label>
                <Input
                  value={editContent.title_en || ''}
                  onChange={(e) => setEditContent({ ...editContent, title_en: e.target.value })}
                  data-testid="edit-profile-title-en"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Titre (Français)' : 'Title (French)'}</Label>
                <Input
                  value={editContent.title_fr || ''}
                  onChange={(e) => setEditContent({ ...editContent, title_fr: e.target.value })}
                  data-testid="edit-profile-title-fr"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Bio (Anglais)' : 'Bio (English)'}</Label>
                <Textarea
                  value={editContent.bio_en || ''}
                  onChange={(e) => setEditContent({ ...editContent, bio_en: e.target.value })}
                  rows={4}
                  data-testid="edit-profile-bio-en"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Bio (Français)' : 'Bio (French)'}</Label>
                <Textarea
                  value={editContent.bio_fr || ''}
                  onChange={(e) => setEditContent({ ...editContent, bio_fr: e.target.value })}
                  rows={4}
                  data-testid="edit-profile-bio-fr"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Ma photo (portrait)' : 'My Photo (portrait)'}</Label>
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' ? 'Photo de vous (affichée à gauche)' : 'Photo of yourself (shown on left)'}
                  </p>
                  <ImageUpload
                    value={editContent.profile_photo ? [getFullImageUrl(editContent.profile_photo)] : []}
                    onChange={(images) => setEditContent({ ...editContent, profile_photo: images[0] || '' })}
                    maxFiles={1}
                    language={language}
                    data-testid="edit-profile-photo"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Photo de propriété' : 'Property Photo'}</Label>
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' ? 'Photo d\'intérieur (affichée à droite)' : 'Interior photo (shown on right)'}
                  </p>
                  <ImageUpload
                    value={editContent.image_url ? [getFullImageUrl(editContent.image_url)] : []}
                    onChange={(images) => setEditContent({ ...editContent, image_url: images[0] || '' })}
                    maxFiles={1}
                    language={language}
                    data-testid="edit-profile-image"
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button className="btn-primary" onClick={handleSaveContent} disabled={saving} data-testid="save-content-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
